/**
 * How long a sign-in lasts, and when it stops early.
 *
 * Two rules, and the second is the interesting one. A session is good for a
 * working day, because being asked to sign in again halfway through an
 * afternoon of reorg planning is the whole complaint this answers. But a day is
 * a long time for a tab on an unlocked laptop, so the last stretch of it is
 * conditional: once the day is nearly over, walking away ends it.
 *
 * Kept apart from `auth.ts` so the arithmetic can be tested without a Google
 * sign-in anywhere near it.
 */

/** The whole allowance. The server signs for the same length. */
export const SESSION_MS = 12 * 60 * 60 * 1000;

/**
 * When idleness starts counting against you.
 *
 * Before this, going for lunch is not a reason to sign somebody out; after it,
 * the session is nearly spent anyway and an unattended tab is the more likely
 * explanation for a quiet half hour than a long meeting.
 */
export const GRACE_MS = 10 * 60 * 60 * 1000;

/** How long a lull has to last, once it counts. */
export const IDLE_MS = 30 * 60 * 1000;

export interface Lifetime {
  /** When the session was issued. */
  startedAt: number;
  /** When it expires outright, as the server signed it. */
  expiresAt: number;
  /** The last time this person did anything. */
  activeAt: number;
}

export type Ending = 'expired' | 'idle' | null;

/**
 * Whether this session is over, and which rule ended it.
 *
 * Returning *which* is not decoration: "your session expired" and "you were
 * signed out after being away" are different things to be told, and being told
 * the wrong one makes the app look broken rather than careful.
 */
export function ending(lifetime: Lifetime, now: number = Date.now()): Ending {
  if (now >= lifetime.expiresAt) return 'expired';
  if (now - lifetime.startedAt >= GRACE_MS && now - lifetime.activeAt >= IDLE_MS) return 'idle';
  return null;
}

/**
 * When to look again.
 *
 * A fixed tick would either burn a timer every few seconds all day or let a
 * session run minutes past its end. This asks for the earliest moment the answer
 * could change, floored so a wildly stale clock cannot produce a busy loop.
 */
export function nextCheck(lifetime: Lifetime, now: number = Date.now()): number {
  const floor = 15_000;
  const untilExpiry = lifetime.expiresAt - now;
  const untilGrace = lifetime.startedAt + GRACE_MS - now;
  // Inside the grace period the next thing that can happen is the idle rule
  // arriving; before it, the earliest is the grace period itself starting.
  const untilIdle = untilGrace > 0 ? untilGrace : lifetime.activeAt + IDLE_MS - now;
  return Math.max(floor, Math.min(untilExpiry, untilIdle));
}
